/**
 * The seam that keeps shared UI primitives off a router.
 *
 * `EmptyState`, `Breadcrumb` and `LookupField` all need client-side
 * navigation, and all three imported `Link` from react-router-dom to get it. That import is what stopped
 * them being extracted into a shared library (OS-20). Since 2026-09-25 the
 * shared `@olytic/ui` is Olytic/olytic-ui and nothing is extracted from
 * Platform (olytic-infra studio-app-platform.md §13), so this seam is
 * transitional: it goes when these screens move onto @olytic/ui. Platform is on react-router-dom
 * ^6.27.0 and studio-fripp is on ^7.18.3, so a library importing `Link`
 * directly would need a peer dependency range that neither studio satisfies
 * jointly, and one of them would have to migrate majors before it could
 * consume a button.
 *
 * So the library never imports a router. It asks for a link component and
 * renders whatever it is handed. Each app wires its own at the root, next to
 * its existing router provider.
 *
 * The default is a plain `<a href>` rather than a thrown error. A consumer
 * that forgets the provider gets a working link that does a full page load —
 * degraded, not broken. Throwing would turn a missed wiring step into a blank
 * screen, and this is a presentational primitive, not a security boundary.
 *
 * Context rather than a prop because `EmptyState` has 33 call sites,
 * `Breadcrumb` 10 and `LookupField` 15. A `linkComponent` prop would push the
 * wiring into all 58;
 * the context puts it in one place and leaves every call site passing `to`
 * exactly as it does today.
 */

import { createContext, type ReactElement, type ReactNode, useContext } from 'react';

export interface UiLinkProps {
  /** Target route, passed through untouched to the injected component. */
  to: string;
  className?: string;
  children: ReactNode;
}

/**
 * A call signature rather than `ComponentType`. `ComponentType` resolves to
 * `FunctionComponent`, which carries a `propTypes` field that is invariant
 * under this repo's `exactOptionalPropertyTypes`, so react-router's `Link` — a
 * `ForwardRefExoticComponent` — fails to assign to it even though it renders
 * perfectly well. The library only needs something it can call and render, so
 * that is all it asks for.
 */
export type UiLinkComponent = (props: UiLinkProps) => ReactElement | null;

/**
 * Router-free fallback. `to` maps to `href`, so it works without any provider
 * and degrades to a full navigation instead of a client-side one.
 */
function AnchorLink({ to, className, children }: UiLinkProps) {
  return (
    <a href={to} className={className}>
      {children}
    </a>
  );
}

const UiLinkContext = createContext<UiLinkComponent>(AnchorLink);

export function UiLinkProvider({ link, children }: { link: UiLinkComponent; children: ReactNode }) {
  return <UiLinkContext.Provider value={link}>{children}</UiLinkContext.Provider>;
}

/** The link component to render. `AnchorLink` when no provider is present. */
export function useUiLink(): UiLinkComponent {
  return useContext(UiLinkContext);
}
